import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class SellerService {
  private baseUrl = environment.URL;
  private api_url = environment.API_URL;

  constructor(private http: HttpClient) {}

  // ── Analytics / Dashboard ─────────────────────────────────────────────────────

  /** POST api/analytics/seller-dashboard-analytics */
  getSellerDashboard(data: any): Observable<any> {
    return this.http.post(`${this.api_url}analytics/seller-dashboard-analytics`, data);
  }

  /** POST api/analytics/consumer-statement-client-view */
  getSellerStatement(data: any): Observable<any> {
    return this.http.post(`${this.api_url}analytics/consumer-statement-client-view`, data);
  }

  // ── Inventory Management ──────────────────────────────────────────────────────

  /** POST api/inventory/list */
  getInventoryListPrivate(data: any): Observable<any> {
    return this.http.post(`${this.api_url}inventory/list`, data);
  }

  /** POST api/inventory/sellermappingbranchList */
  getSellerMappingBranchList(data: any): Observable<any> {
    return this.http.post(`${this.api_url}inventory/sellermappingbranchList`, data);
  }

  /** POST api/inventory/checkUniqueVin */
  checkUniqueVin(data: any): Observable<any> {
    return this.http.post(`${this.api_url}inventory/checkUniqueVin`, data);
  }

  /** POST inventory/add */
  addInventory(data: any): Observable<any> {
    return this.http.post(`${this.api_url}inventory/add`, data);
  }

  /** PATCH inventory/update */
  updateInventory(data: any): Observable<any> {
    return this.http.patch(`${this.api_url}inventory/update`, data);
  }

  /** POST inventory/inventoryCopy */
  copyInventory(data: any): Observable<any> {
    return this.http.post(`${this.api_url}inventory/inventoryCopy`, data);
  }

  /** PATCH inventory/initiate-reauction-request/:inventoryId */
  requestReauction(inventoryId: string, data: any): Observable<any> {
    return this.http.patch(`${this.api_url}inventory/initiate-reauction-request/${inventoryId}`, data);
  }

  /** POST inventory/getPassList */
  getPassList(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.api_url}inventory/getPassList`, data, { headers });
  }

  // ── Reserve Price ─────────────────────────────────────────────────────────────

  /** POST inventory/addreseverprice */
  addReservePrice(data: any): Observable<any> {
    return this.http.post(`${this.api_url}inventory/addreseverprice`, data);
  }

  /** PATCH inventory/selleracceptanceUpdate */
  updateReservePrice(data: any): Observable<any> {
    return this.http.patch(`${this.api_url}inventory/selleracceptanceUpdate`, data);
  }

  /** PATCH inventory/inventoryRequestUpdate */
  approveInventory(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.patch(`${this.api_url}inventory/inventoryRequestUpdate`, data, { headers });
  }

  /** POST inventory/send-reserve-price-confirmation-otp */
  sendReservePriceOtp(data: any): Observable<any> {
    return this.http.post(`${this.api_url}inventory/send-reserve-price-confirmation-otp`, data);
  }

  /** POST inventory/update-reserve-price-up-to-offered */
  updateReservePriceUpTo(data: any): Observable<any> {
    return this.http.post(`${this.api_url}inventory/update-reserve-price-up-to-offered`, data);
  }

  /** POST inventory/send-reserve-price-confirmation-otp */
  sendReservePriceConfirmationOtp(data: any): Observable<any> {
    return this.http.post(`${this.api_url}inventory/send-reserve-price-confirmation-otp`, data);
  }

  /** PATCH inventory/initiate-reauction-request/:inventoryId */
  requestForReauction(inventoryId: any, data: any): Observable<any> {
    return this.http.patch(`${this.api_url}inventory/initiate-reauction-request/${inventoryId}`, data);
  }

  // ── Receipt / Financial ───────────────────────────────────────────────────────

  /** POST api/receipt/servicechargelist */
  getServiceChargeList(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}api/receipt/servicechargelist`, data);
  }

  /** GET api/receipt/receipt-list-dashboard */
  getReceiptListDashboard(data: any): Observable<any> {
    return this.http.get(`${this.baseUrl}api/receipt/receipt-list-dashboard`, { params: data });
  }

  /** POST api/receipt/receipt-list */
  getSoldVehicleList(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}api/receipt/receipt-list`, data);
  }

  /** POST api/receipt/sellerpaybalelist */
  getTransactionList(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}api/receipt/sellerpaybalelist`, data);
  }

  /** POST api/analytics/consumer-statement-client-view */
  getStatement(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}api/account/consumer-statement-client-view`, data);
  }

  /** POST salereciept/get-invoice */
  sellerInvoiceData(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.api_url}receipt/get-invoice`, data, { headers });
  }

  /** POST account/gltempList */
  accountGlTempList(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.api_url}account/gltempList`, data, { headers });
  }

  /** PATCH reciept/update/:id */
  updateReceipt(id: string, data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.patch(`${this.api_url}receipt/update/${id}`, data, { headers });
  }

  /** POST receipt/add-customer-payment */
  addCustomerPayment(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.baseUrl}reciept/add-customer-payment`, data, { headers });
  }

  /** POST reciept/addLedger */
  addLedgerPayment(data: any): Observable<any> {
    return this.http.post(`${this.api_url}receipt/addLedger`, data);
  }

  /** POST salereciept/get-invoice */
  getSellerInvoice(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.api_url}receipt/get-invoice`, data, { headers });
  }

  /** POST account/glListReport */
  getAccountGlList(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}account/glListReport`, data);
  }

  /** POST account/gltempList */
  getAccountGlTempList(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.baseUrl}account/gltempList`, data, { headers });
  }

  // ── Shipping ──────────────────────────────────────────────────────────────────

  /** POST shipping/uploadShippingImages */
  addShippingImages(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}shipping/uploadShippingImages`, data);
  }

  /** POST shipping/getShipmenetDetails */
  getShippingDetails(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}shipping/getShipmenetDetails`, data);
  }

  // ── Seller Plan ───────────────────────────────────────────────────────────────

  /** POST master/seller-plan-list */
  getSellerPlanList(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.baseUrl}master/seller-plan-list`, data, { headers });
  }
}
